/**
 * SHA-256 of a file, as lowercase hex — the form the service stores and compares.
 *
 * Bills are uploaded straight to S3 now, so the service never sees the bytes and cannot hash them
 * itself. Without a hash from here a file uploaded twice lands as two live bills rather than the
 * second one showing as a duplicate, and neither copy can be matched by anything arriving later
 * either — an email carrying the same attachment included (LB-5898).
 */

/**
 * Resolves with the hash, or with `null` when this browser cannot produce one.
 *
 * Null rather than a throw, and the caller uploads anyway. `crypto.subtle` needs a secure context,
 * which every environment this ships to is — but it is absent under some test runners and behind a
 * few corporate proxies that serve the app over plain http. Losing duplicate detection for those
 * sessions is a far smaller thing than refusing to accept their bills.
 */
export async function sha256Hex(file) {
  const subtle = typeof crypto !== 'undefined' ? crypto.subtle : undefined;
  if (!(subtle !== null && subtle !== void 0 && subtle.digest) || typeof file.arrayBuffer !== 'function') return null;
  try {
    const digest = await subtle.digest('SHA-256', await file.arrayBuffer());

    // Byte by byte rather than via a string-encoding round trip, which would mangle any byte that
    // is not printable. Padded because a byte below 0x10 renders as one character, and 63 characters
    // is not a hash — it would be stored as nothing and silently match nothing.
    return Array.from(new Uint8Array(digest)).map(byte => byte.toString(16).padStart(2, '0')).join('');
  } catch (_unused) {
    // A digest that fails is a browser that cannot do it, which is the same answer as not having it.
    return null;
  }
}